import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Info, Loader2, MapPin, Navigation } from "lucide-react";

import { api } from "@/api/client";
import { MkButton } from "@/mk/MkButton";
import { MkInput } from "@/mk/MkInput";
import { MkModal } from "@/mk/MkModal";

import { useUserLocation } from "./useUserLocation";
import type { PlaceResult, UserLocation } from "./useUserLocation";

const SOURCE_KEYS: Record<string, string> = {
    gps: "home.location.source.gps",
    manual: "home.location.source.manual",
    default: "home.location.source.default",
};

interface Props {
    open: boolean;
    current?: UserLocation | null;
    onClose(): void;
}

export function PmLocationModal({ open, current, onClose }: Props) {
    const { t } = useTranslation();
    const { save } = useUserLocation({ autoDetect: false });
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<PlaceResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [locating, setLocating] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) {
            return;
        }
        setSearch("");
        setResults([]);
        setError("");
    }, [open]);

    useEffect(() => {
        const term = search.trim();
        if (term.length < 2) {
            setResults([]);
            setSearching(false);
            return;
        }
        const controller = new AbortController();
        const timer = window.setTimeout(() => {
            setSearching(true);
            setError("");
            api.raw<PlaceResult[]>(`/me/location/search?q=${encodeURIComponent(term)}`,
                                   controller.signal)
                .then((payload) => setResults(payload.data ?? []))
                .catch((failure: Error) => {
                    if (failure.name !== "AbortError") {
                        setError(t("home.location.searchFailed"));
                    }
                })
                .finally(() => setSearching(false));
        }, 450);
        return () => {
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [search, t]);

    async function locateMe() {
        if (!window.isSecureContext || !navigator.geolocation) {
            setError(t("home.location.secureOnly"));
            return;
        }
        setLocating(true);
        setError("");
        navigator.geolocation.getCurrentPosition(async (position) => {
            const latitude = Math.round(position.coords.latitude * 1e6) / 1e6;
            const longitude = Math.round(position.coords.longitude * 1e6) / 1e6;
            try {
                const place = await api.raw<PlaceResult>(
                    `/me/location/reverse?lat=${latitude}&lon=${longitude}`);
                if (!place.data?.city) {
                    throw new Error("no city");
                }
                await save({
                    city: place.data.city,
                    latitude,
                    longitude,
                    timezone: place.data.timezone,
                    source: "gps",
                });
                onClose();
            } catch {
                setError(t("home.location.saveFailed"));
            } finally {
                setLocating(false);
            }
        }, () => {
            setLocating(false);
            setError(t("home.location.gpsFailed"));
        }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
    }

    async function choose(result: PlaceResult) {
        setSaving(true);
        setError("");
        try {
            await save({
                city: result.city || result.label.split(",")[0] || result.label,
                latitude: result.latitude,
                longitude: result.longitude,
                source: "manual",
            });
            onClose();
        } catch {
            setError(t("home.location.saveFailed"));
        } finally {
            setSaving(false);
        }
    }

    const sourceLabel = current?.source
        ? t(SOURCE_KEYS[current.source] ?? "home.location.source.manual")
        : t("home.location.noneHint");

    return (
        <MkModal open={open} size="md" onClose={onClose}
                 title={t("home.location.title")} subtitle={t("home.location.subtitle")}
                 footer={<MkButton variant="ghost" label={t("mk.close")} onClick={onClose} />}>
            <div className="pm-location">
                <div className="pm-location-current">
                    <span className="pm-location-pin"><MapPin size={20} aria-hidden="true" /></span>
                    <div className="pm-location-current-text">
                        <strong>{current?.city || t("home.location.none")}</strong>
                        <span>{sourceLabel}</span>
                    </div>
                </div>

                <div className="pm-location-actions">
                    <MkButton variant="primary" label={t("home.location.gps")}
                              loading={locating} onClick={() => void locateMe()}>
                        <Navigation size={15} aria-hidden="true" />
                        {t("home.location.gps")}
                    </MkButton>
                </div>

                {error && <div className="pm-location-error">{error}</div>}

                <div className="pm-location-search">
                    <MkInput label={t("home.location.search")}
                             value={search}
                             placeholder={t("home.location.searchPlaceholder")}
                             onChange={setSearch} />
                    {searching && (
                        <div className="pm-location-note">
                            <Loader2 size={14} className="pm-location-spin" aria-hidden="true" />
                            <span>{t("home.location.searching")}</span>
                        </div>
                    )}
                    {!searching && search.trim().length >= 2 && !results.length && (
                        <div className="pm-location-note">{t("home.location.empty")}</div>
                    )}
                    {results.length > 0 && (
                        <ul className="pm-location-results">
                            {results.map((result, index) => (
                                <li key={`${result.latitude},${result.longitude},${index}`}>
                                    <button type="button" className="pm-location-result"
                                            disabled={saving} onClick={() => void choose(result)}>
                                        <span className="pm-location-result-icon">
                                            <MapPin size={16} aria-hidden="true" />
                                        </span>
                                        <span className="pm-location-result-text">
                                            <strong>{result.city || result.label.split(",")[0]}</strong>
                                            <small>{result.label}</small>
                                        </span>
                                        {saving
                                            ? <Loader2 size={14} className="pm-location-spin"
                                                       aria-hidden="true" />
                                            : <Check size={14} aria-hidden="true" />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="pm-location-hint">
                    <Info size={13} aria-hidden="true" />
                    <span>{t("home.location.hint")}</span>
                </div>
            </div>
        </MkModal>
    );
}
